import { InventoryCountItem } from './stock.types';

export interface CountApplyWrite {
  stockItemId: string;
  /** `c − stockAtStart` (la variación descubierta). El clamp a `−liveQty` lo aplica el caller al escribir. */
  delta: number;
}

export interface CountApplyCreate {
  stockItemId: string;
  /** Stock inicial del nuevo nodo `productStock` = lo contado (`c`). El trigger abre el Kardex con esta apertura. */
  qty: number;
}

export interface CountApplyResult {
  /** Ítems ya controlados al iniciar (`stockAtStart` número): `increment(delta)` delta-safe, incluido el mojón var-0. */
  writes: CountApplyWrite[];
  /** Ítems recién puestos bajo control (contados, sin control al iniciar): CREAR `productStock` en `qty`. */
  creates: CountApplyCreate[];
  /** Ítems dados de BAJA de control (tenían control al iniciar, ahora destildados y sin contar): persistir su `productStock` con `stockQty:null` (conserva historial; NO borrar). */
  controlDisabled: string[];
}

/**
 * Deriva las operaciones de aplicar un conteo desde su mapa `stockItems`, discriminando por el **baseline
 * congelado en el snapshot** (`snapshotStock`: id → stock al iniciar; `null` = sin control al iniciar). Esto
 * reemplaza a la vieja discriminación por "nodo vivo": el snapshot dice de forma estable e inmutable si el
 * ítem tenía control al iniciar, sin depender de una foto de `productStock` con carreras.
 *
 * - **contado + tenía control** (`stockAtStart` número) → `write` con `delta = c − stockAtStart` (incluido el
 *   mojón var-0). El clamp `max(delta, −liveQty)` lo aplica el caller al escribir (única lectura de stock vivo).
 * - **contado + sin control al iniciar** (`stockAtStart === null`) → `create` en `qty = c` (alta de control).
 * - **sin contar + tenía control + destildado (`e === null`)** → `controlDisabled` (baja de control:
 *   persiste `stockQty:null`, conserva historial; el trigger registra un movimiento `control-disabled`).
 * - resto → nada.
 *
 * POR QUÉ DELTA Y NO SET-ABSOLUTO (decisión de negocio): lo que el conteo aplica es la **variación
 * descubierta** (`c − stockAtStart`), no un valor absoluto. Si hubo ventas mientras se contaba, esas son
 * movimientos legítimos e independientes que ya bajaron el stock vivo; el conteo solo corrige el
 * faltante/sobrante que el operador encontró, sumándolo al stock vivo. El único ajuste al escribir es el clamp
 * para no dejar stock negativo. Sin supersede (DA-6). Puro: el caller arma los writes de Firestore
 * (increment/set), resuelve `productId`/`entityType` desde el snapshot y filtra los ya-aplicados por `a`.
 */
export function computeCountApply(
  stockItems: Record<string, InventoryCountItem>,
  snapshotStock: Map<string, number | null>,
): CountApplyResult {
  const writes: CountApplyWrite[] = [];
  const creates: CountApplyCreate[] = [];
  const controlDisabled: string[] = [];

  for (const [stockItemId, { e, c }] of Object.entries(stockItems)) {
    const stockAtStart = snapshotStock.get(stockItemId) ?? null;

    if (c === null) {
      if (stockAtStart !== null && e === null) {
        controlDisabled.push(stockItemId);
      }
      continue;
    }

    if (stockAtStart === null) {
      creates.push({ stockItemId, qty: c });
      continue;
    }

    writes.push({ stockItemId, delta: c - stockAtStart });
  }

  return { writes, creates, controlDisabled };
}
